/**
 * Staff Action Centre — Process Walkthroughs tab (Compliance / Design).
 *
 * Variant-aware execution UX:
 *
 *   Compliance:
 *     Per step → Complied / NotComplied (+ remarks).
 *     Step display includes step_type, accountable authority, timeline.
 *
 *   Design:
 *     Per step → Pass / Fail / NA (+ remarks).
 *     Each step expands into linked Controls → Risks + Implementations (read-
 *     only cascade derived live from the maps).
 */
import React, { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Send, Check, X, Search, MessageSquare } from 'lucide-react';
import Skeleton from '../../../components/common/Skeleton';
import {
    SeamlessSurface,
    SeamlessCard,
    TypeChip,
    SectionLabel,
    AssessmentPanel,
    DualScorePanel,
    AssessRow,
    NetScore,
    ImplList,
    StatusButtons,
    riskBand,
    scoreBand,
} from '../../../components/audits/procedure-responses/seamless/SeamlessKit';
import {
    sopProgramService,
    SopProgram,
    SopProgramDetail,
    ProgramStepRow,
    CascadeControl,
    CascadeRisk,
    StepResult,
    SopVariant,
} from '../../../services/sopProgramService';
import Modal from '../../../components/common/Modal';
import InlineIssueMeta from '../../../components/common/InlineIssueMeta';
import { riskVulnerabilityService, RiskVulnerability } from '../../../services/riskVulnerabilityService';
import { controlDeficiencyService, ControlDeficiency } from '../../../services/controlDeficiencyService';
import Button from '../../../components/common/Button';
import FormControl from '../../../components/common/FormControl';
import Pagination from '../../../components/common/Pagination';
import NoDataFound from '../../../components/common/NoDataFound';
import Toast, { ToastMessage } from '../../../components/common/Toast';
import extractApiError from '../../../utils/apiError';
import { statusBadgeCss } from '../../../utils/statusVariant';
import { useAuth } from '../../../contexts/AuthContext';
import { useEntitySettings } from '../../../contexts/EntitySettingsContext';
import { validate } from '../../../validation/engine';
import { visibleForScope, isClosedProgram } from './workArchive';

type ImplVerdict = 'Pass' | 'Fail' | 'NA' | '';
/** Editable risk-card sliders (Inherent L×S + risk-level Ex×Ef → client residual). */
interface SopRiskLocal { likelihood: number | ''; impact: number | ''; existence: number | ''; effectiveness: number | ''; }
/** Editable control-card sliders (Existence × Effectiveness). */
interface SopCtrlLocal { existence: number | ''; effectiveness: number | ''; }
/** Editable implementation verdict (Pass/Fail/NA). */
interface SopImplLocal { result: ImplVerdict; }

const EMPTY_RISK: SopRiskLocal = { likelihood: '', impact: '', existence: '', effectiveness: '' };
const EMPTY_CTRL: SopCtrlLocal = { existence: '', effectiveness: '' };

/** A risk node after the client-side regroup of step.controls[].risks[] —
 *  risk-first, deduped by risk_id, accumulating every control that maps to it. */
interface RegroupedRisk { risk: CascadeRisk; controls: CascadeControl[]; }

/**
 * Invert a step's control-keyed cascade (step.controls[].risks[]) into a
 * risk-first list. A control appears under every risk it mitigates; a risk
 * shared by two controls collapses to ONE node (dedupe by risk_id) that
 * accumulates both controls. Implementations travel nested on the control.
 * Mirrors RiskAssessmentResponse's regroup (CONTEXT decision 2).
 */
const regroupRisks = (controls: CascadeControl[] | undefined): RegroupedRisk[] => {
    const byRisk = new Map<number, RegroupedRisk>();
    for (const c of controls ?? []) {
        for (const r of c.risks ?? []) {
            let node = byRisk.get(r.risk_id);
            if (!node) { node = { risk: r, controls: [] }; byRisk.set(r.risk_id, node); }
            if (!node.controls.some(x => x.control_id === c.control_id)) node.controls.push(c);
        }
    }
    return Array.from(byRisk.values());
};

const ExecutionModal: React.FC<{
    programId: number;
    isChecker: boolean;
    readOnly?: boolean;
    onClose: () => void;
    onSaved: () => void;
}> = ({ programId, isChecker, readOnly = false, onClose, onSaved }) => {
    const { formatDate } = useEntitySettings();
    const [detail, setDetail] = useState<SopProgramDetail | null>(null);
    const [loading, setLoading] = useState(true);
    const [savingId, setSavingId] = useState<number | null>(null);
    const [busy, setBusy] = useState(false);
    const [toast, setToast] = useState<ToastMessage | null>(null);
    const [obsErrors, setObsErrors] = useState<Record<number, string>>({});
    // Observation modal (checklist-parity) keyed by step score_id.
    const [remarksStep, setRemarksStep] = useState<number | null>(null);
    // Checker reject flow — capture a reason (sent to the maker on bounce-back).
    const [showReject, setShowReject] = useState(false);
    const [rejectReason, setRejectReason] = useState('');
    const [rejectErr, setRejectErr] = useState<string | null>(null);
    // Design-variant nested score state — flat maps keyed on the step's score_id
    // (step_score_id) so the same risk/control under two steps stays independent.
    //   riskState  key `${score_id}-${risk_id}`
    //   ctrlState  key `${score_id}-${risk_id}-${control_id}`
    //   implState  key `${score_id}-${implementation_id}`
    const [riskState, setRiskState] = useState<Record<string, SopRiskLocal>>({});
    const [ctrlState, setCtrlState] = useState<Record<string, SopCtrlLocal>>({});
    const [implState, setImplState] = useState<Record<string, SopImplLocal>>({});
    // Vulnerabilities (per risk) / deficiencies (per control) raised on the
    // cascade nodes — shown inline under each node for audit parity (Design only).
    const [vulnsByRisk, setVulnsByRisk] = useState<Record<number, RiskVulnerability[]>>({});
    const [defsByControl, setDefsByControl] = useState<Record<number, ControlDeficiency[]>>({});

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const d = await sopProgramService.get(programId);
            setDetail(d);
            // Seed the Design nested-score local state from the saved scores
            // returned per step (flat lists, joined by id here).
            const rState: Record<string, SopRiskLocal> = {};
            const cState: Record<string, SopCtrlLocal> = {};
            const iState: Record<string, SopImplLocal> = {};
            for (const s of d.steps ?? []) {
                for (const rs of s.risk_scores ?? []) {
                    rState[`${s.score_id}-${rs.risk_id}`] = {
                        likelihood: rs.likelihood_score ?? '',
                        impact: rs.impact_score ?? '',
                        existence: rs.existence_score ?? '',
                        effectiveness: rs.effectiveness_score ?? '',
                    };
                }
                for (const cs of s.control_scores ?? []) {
                    cState[`${s.score_id}-${cs.risk_id}-${cs.control_id}`] = {
                        existence: cs.existence_score ?? '',
                        effectiveness: cs.effectiveness_score ?? '',
                    };
                }
                for (const is of s.impl_scores ?? []) {
                    iState[`${s.score_id}-${is.implementation_id}`] = {
                        result: (is.result as ImplVerdict) ?? '',
                    };
                }
            }
            setRiskState(rState);
            setCtrlState(cState);
            setImplState(iState);
            // Design cascade carries risk/control nodes that can hold raised
            // vulnerabilities/deficiencies. Batch-load them (de-duped by id) so
            // they render inline like audit findings. Compliance has no cascade.
            if (d.variant !== 'Compliance') {
                const riskIds = new Set<number>();
                const controlIds = new Set<number>();
                for (const s of d.steps ?? []) {
                    for (const c of s.controls ?? []) {
                        if (c.control_id != null) controlIds.add(c.control_id);
                        for (const r of c.risks ?? []) {
                            if (r.risk_id != null) riskIds.add(r.risk_id);
                        }
                    }
                }
                const [vEntries, dEntries] = await Promise.all([
                    Promise.all(Array.from(riskIds).map(async id => {
                        try { return [id, await riskVulnerabilityService.listForRisk(id)] as const; }
                        catch { return [id, [] as RiskVulnerability[]] as const; }
                    })),
                    Promise.all(Array.from(controlIds).map(async id => {
                        try { return [id, await controlDeficiencyService.listForControl(id)] as const; }
                        catch { return [id, [] as ControlDeficiency[]] as const; }
                    })),
                ]);
                setVulnsByRisk(Object.fromEntries(vEntries));
                setDefsByControl(Object.fromEntries(dEntries));
            }
        } catch (e: any) {
            setToast({ type: 'error', message: extractApiError(e, 'Failed to load program') });
        } finally { setLoading(false); }
    }, [programId]);
    useEffect(() => { load(); }, [load]);

    const isCompliance = detail?.variant === 'Compliance';
    const verbOptions: StepResult[] = isCompliance ? ['Complied', 'NotComplied'] : ['Pass', 'Fail', 'NA'];
    // Approved is terminal — nobody (maker or checker) may edit once approved.
    const isApproved = detail?.status === 'Approved';
    // Once the maker submits (status Complete) the program is frozen for edits
    // until a checker bounces it back — the maker must not keep changing
    // results/scores after submitting. Complete and Approved both lock inputs.
    const isSubmitted = detail?.status === 'Complete' || detail?.status === 'Approved';
    const locked = readOnly || isChecker || isSubmitted;

    const updateStep = async (s: ProgramStepRow, patch: Partial<ProgramStepRow>) => {
        setSavingId(s.score_id);
        try {
            await sopProgramService.updateStepScore(programId, s.score_id, patch as any);
            setDetail(prev => prev ? {
                ...prev,
                steps: prev.steps.map(x => x.score_id === s.score_id ? { ...x, ...patch } : x),
            } : prev);
        } catch (e: any) {
            setToast({ type: 'error', message: extractApiError(e, 'Failed to save') });
        } finally { setSavingId(null); }
    };

    // Observations text → validation code 110 (optional). On blur: validate;
    // if invalid set per-row error (keyed by score_id) and DON'T autosave; if
    // valid (or empty) clear the error and persist the remarks.
    const handleObsBlur = (s: ProgramStepRow) => {
        const value = s.remarks || '';
        if (value.trim()) {
            const r = validate(110, value);
            if (!r.valid) {
                setObsErrors(prev => ({ ...prev, [s.score_id]: r.message ?? 'Invalid observation.' }));
                return;
            }
        }
        setObsErrors(prev => { const n = { ...prev }; delete n[s.score_id]; return n; });
        updateStep(s, { remarks: value });
    };

    // --- Design nested-score save-on-change (mirror updateStep: optimistic local
    // state + PUT; on error revert via reload + toast). Each carries the step's
    // score_id as step_score_id so per-step independence is preserved. ---------
    const num = (v: number | '' | undefined): number | null => (v === '' || v == null ? null : v);

    const saveRisk = (s: ProgramStepRow, riskId: number, patch: Partial<SopRiskLocal>) => {
        const key = `${s.score_id}-${riskId}`;
        const next: SopRiskLocal = { ...EMPTY_RISK, ...riskState[key], ...patch };
        setRiskState(prev => ({ ...prev, [key]: next }));
        sopProgramService.saveRiskScore(programId, {
            step_score_id: s.score_id,
            process_step_id: s.process_step_id,
            risk_id: riskId,
            likelihood_score: num(next.likelihood),
            impact_score: num(next.impact),
            existence_score: num(next.existence),
            effectiveness_score: num(next.effectiveness),
        }).catch((e: any) => setToast({ type: 'error', message: extractApiError(e, 'Failed to save risk score') }));
    };

    const saveCtrl = (s: ProgramStepRow, riskId: number, controlId: number, patch: Partial<SopCtrlLocal>) => {
        const key = `${s.score_id}-${riskId}-${controlId}`;
        const next: SopCtrlLocal = { ...EMPTY_CTRL, ...ctrlState[key], ...patch };
        setCtrlState(prev => ({ ...prev, [key]: next }));
        sopProgramService.saveControlScore(programId, {
            step_score_id: s.score_id,
            risk_id: riskId,
            control_id: controlId,
            existence_score: num(next.existence),
            effectiveness_score: num(next.effectiveness),
        }).catch((e: any) => setToast({ type: 'error', message: extractApiError(e, 'Failed to save control score') }));
    };

    const saveImpl = (s: ProgramStepRow, controlId: number, implementationId: number, result: ImplVerdict) => {
        const key = `${s.score_id}-${implementationId}`;
        setImplState(prev => ({ ...prev, [key]: { result } }));
        sopProgramService.saveImplScore(programId, {
            step_score_id: s.score_id,
            control_id: controlId,
            implementation_id: implementationId,
            result: result === '' ? null : (result as StepResult),
        }).catch((e: any) => setToast({ type: 'error', message: extractApiError(e, 'Failed to save implementation result') }));
    };

    const total = detail?.steps.length ?? 0;
    const done = detail?.steps.filter(s => s.result).length ?? 0;
    const allDone = total > 0 && done === total;

    const submit = async () => {
        setBusy(true);
        try {
            await sopProgramService.submit(programId);
            setToast({ type: 'success', message: 'Program submitted for approval.' });
            onSaved(); onClose();
        } catch (e: any) {
            setToast({ type: 'error', message: extractApiError(e, 'Failed to submit') });
        } finally { setBusy(false); }
    };

    const approve = async () => {
        setBusy(true);
        try {
            await sopProgramService.approve(programId);
            setToast({ type: 'success', message: 'Program approved.' });
            // Keep the modal open and reload so the footer flips to the locked
            // "Approved" label in place; refresh the list behind it too.
            await load();
            onSaved();
        } catch (e: any) {
            setToast({ type: 'error', message: extractApiError(e, 'Failed to approve') });
        } finally { setBusy(false); }
    };

    const reject = async () => {
        const reason = rejectReason.trim();
        if (!reason) { setRejectErr('A reason is required to reject.'); return; }
        const r = validate(110, reason);
        if (!r.valid) { setRejectErr(r.message ?? 'Invalid reason.'); return; }
        setBusy(true);
        try {
            await sopProgramService.reject(programId, reason);
            setToast({ type: 'success', message: 'Program rejected and returned to maker.' });
            setShowReject(false);
            setRejectReason('');
            await load();
            onSaved();
            onClose();
        } catch (e: any) {
            setToast({ type: 'error', message: extractApiError(e, 'Failed to reject') });
        } finally { setBusy(false); }
    };

    return (
        <Modal isOpen onClose={onClose} size="xl" closeOnOverlayClick={false}>
            <Modal.Header
                onClose={onClose}
                titleAction={detail ? (
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 10 }}>
                        <span className={statusBadgeCss(detail.status)}>{detail.status}</span>
                        <span className="exec-modal-progress">{done} / {total} done</span>
                    </span>
                ) : undefined}
                actions={detail ? (
                    <div className="exec-modal-parties">
                        <span className="exec-modal-party">
                            <span className="exec-modal-party-label">Checker:</span>
                            <span className="exec-modal-party-value">{detail.checker_name ?? '—'}</span>
                        </span>
                        <span className="exec-modal-party">
                            <span className="exec-modal-party-label">Maker:</span>
                            <span className="exec-modal-party-value">{detail.maker_name ?? '—'}</span>
                        </span>
                        {(detail.start_date || detail.end_date) && (
                            <span className="exec-modal-party">
                                <span className="exec-modal-party-label">Dates:</span>
                                <span className="exec-modal-party-value">{formatDate(detail.start_date)} → {formatDate(detail.end_date)}</span>
                            </span>
                        )}
                    </div>
                ) : undefined}
            >
                {detail
                    ? <>Test of {detail.variant} — "<span className="item-highlight">{detail.program_name}</span>"</>
                    : 'Loading…'}
            </Modal.Header>
            <Modal.Body>
                {loading && (
                    <div className="response-section">
                        <div className="table-list response-qtable">
                            <div className="table-list-scroll">
                                <table className="table-list-table">
                                    <colgroup>
                                        <col className="response-col-sno" />
                                        <col />
                                        <col className="response-col-answer" />
                                        <col className="response-col-actions" />
                                    </colgroup>
                                    <thead><tr>
                                        <th className="table-list-th response-th-sno">S.No</th>
                                        <th className="table-list-th">Step</th>
                                        <th className="table-list-th">Status</th>
                                        <th className="table-list-th table-list-th--right">Actions</th>
                                    </tr></thead>
                                    <tbody aria-busy={true} aria-live="polite">
                                        <Skeleton.TableRows cols={['2xs', 'lg', 'md', { width: 'icon', align: 'right' }]} />
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </div>
                )}
                {!loading && detail && (
                    <div className="collapse-section-stack">
                        {/* Checker's reject note — shown to the maker after a bounce-back
                            so they know what to fix. Hidden for the checker and once
                            re-approved. */}
                        {!isChecker && detail.rejection_reason && detail.status !== 'Approved' && (
                            <div className="response-inline-warning" style={{ margin: '0 0 12px', display: 'flex', gap: 8, alignItems: 'flex-start' }} data-testid="BANNER_sop_reject_note">
                                <X size={16} style={{ flexShrink: 0, marginTop: 2 }} />
                                <span><strong>Rejected by checker:</strong> {detail.rejection_reason}</span>
                            </div>
                        )}
                        {/* Compliance variant — UNCHANGED flat step table. */}
                        {isCompliance && (
                        <div className="response-section">
                            <div className="table-list response-qtable">
                                <div className="table-list-scroll">
                                    <table className="table-list-table">
                                        <colgroup>
                                            <col className="response-col-sno" />
                                            <col />
                                            <col className="response-col-answer" />
                                            <col className="response-col-actions" />
                                        </colgroup>
                                        <thead>
                                            <tr>
                                                <th className="table-list-th response-th-sno">S.No</th>
                                                <th className="table-list-th">Step</th>
                                                <th className="table-list-th">Status</th>
                                                <th className="table-list-th table-list-th--right">Actions</th>
                                            </tr>
                                        </thead>
                                        <tbody aria-live="polite">
                                            {detail.steps.map((s, idx) => {
                                                const radioName = `sop-${s.score_id}`;
                                                const sub = [
                                                    s.authority_name,
                                                    s.timeline_days != null ? `${s.timeline_days}d ${s.timeline_anchor || ''}`.trim() : null,
                                                ].filter(Boolean).join(' · ');
                                                return (
                                                    <React.Fragment key={s.score_id}>
                                                        <tr className="table-list-row">
                                                            <td className="table-list-cell response-cell-sno">{idx + 1}</td>
                                                            <td className="table-list-cell response-cell-question">
                                                                <span className="table-list-cell-title">
                                                                    {s.step_name}
                                                                    {s.step_type && <span style={{ marginLeft: 8 }}><TypeChip label={s.step_type} /></span>}
                                                                </span>
                                                                {sub && <span className="table-list-cell-sub">{sub}</span>}
                                                                {/* Observation entered via the remarks popup shows inline under the
                                                                    step (same InlineIssueMeta "Remarks:" pattern as the risk nodes)
                                                                    so the checker/maker reads it without opening the popup. */}
                                                                <InlineIssueMeta
                                                                    label="Observations"
                                                                    issues={[]}
                                                                    remark={s.remarks}
                                                                    remarkLabel="Observation:"
                                                                    testIdPrefix={`CHIP_sop_obs_${s.score_id}`}
                                                                />
                                                            </td>
                                                            <td className="table-list-cell">
                                                                <div className="response-radio-group">
                                                                    {verbOptions.map(v => (
                                                                        <FormControl.Radio
                                                                            key={v}
                                                                            name={radioName}
                                                                            value={v}
                                                                            label={v === 'NotComplied' ? 'Not Complied' : v}
                                                                            checked={s.result === v}
                                                                            onChange={() => { if (!locked) updateStep(s, { result: v }); }}
                                                                            disabled={locked || savingId === s.score_id}
                                                                            data-testid={`BTN_sop_step_${s.score_id}_${v.toLowerCase()}`}
                                                                        />
                                                                    ))}
                                                                </div>
                                                            </td>
                                                            <td className="table-list-cell table-list-cell--actions">
                                                                <div className="response-action-cluster">
                                                                    <span className="response-remarks-cell">
                                                                        <button
                                                                            type="button"
                                                                            className={`response-row-action${s.remarks ? ' response-row-action--filled' : ''}`}
                                                                            title={s.remarks ? 'Edit observations' : 'Add observations'}
                                                                            data-testid={`BTN_sop_step_score_${s.score_id}_remarks`}
                                                                            onClick={() => setRemarksStep(s.score_id)}
                                                                            disabled={locked}
                                                                        >
                                                                            <MessageSquare size={16} />
                                                                        </button>
                                                                        {s.remarks && <span className="response-action-dot response-action-dot--brand" aria-hidden="true" />}
                                                                    </span>
                                                                </div>
                                                            </td>
                                                        </tr>
                                                    </React.Fragment>
                                                );
                                            })}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        </div>
                        )}

                        {/* Design variant — nested SeamlessKit scoring tree:
                            Sub-process (Pass/Fail/NA + remarks) → Risk (Inherent L×S
                            + risk-level Ex×Ef → Residual) → Control (Ex×Ef /25) →
                            Implementations (Pass/Fail/NA). Risks regrouped risk-first
                            from the control-keyed cascade, deduped by risk_id. */}
                        {!isCompliance && (
                            <div className="seamless-tight">
                            <SeamlessSurface>
                                {detail.steps.map((s, idx) => {
                                    const risks = regroupRisks(s.controls);
                                    return (
                                        <SeamlessCard
                                            key={s.score_id}
                                            sno={idx + 1}
                                            snoLabel={`${idx + 1}.`}
                                            variant="subprocess"
                                            title={s.step_name ?? 'Sub-process'}
                                            sub={[
                                                s.authority_name,
                                                s.timeline_days != null ? `${s.timeline_days}d ${s.timeline_anchor || ''}`.trim() : null,
                                            ].filter(Boolean).join(' · ') || null}
                                            typeChip={<TypeChip label={s.step_type} />}
                                            inlineRemark={
                                                // Step observation shown inline under the title (same
                                                // pattern as the risk Vulnerabilities / control
                                                // Deficiencies blocks), labelled "Observation:".
                                                <InlineIssueMeta
                                                    label="Observations"
                                                    issues={[]}
                                                    remark={s.remarks}
                                                    remarkLabel="Observation:"
                                                    testIdPrefix={`CHIP_sop_obs_${s.score_id}`}
                                                />
                                            }
                                            assessment={
                                                <AssessmentPanel>
                                                    <StatusButtons
                                                        options={[
                                                            { val: 'Pass', label: 'Pass', mod: 'pass' },
                                                            { val: 'Fail', label: 'Fail', mod: 'fail' },
                                                            { val: 'NA', label: 'NA', mod: 'na' },
                                                        ]}
                                                        value={(s.result as string) ?? ''}
                                                        disabled={locked || savingId === s.score_id}
                                                        onChange={(val) => updateStep(s, { result: (val === '' ? null : val) as StepResult | null })}
                                                        testId={(val) => `BTN_sop_step_${s.score_id}_${val.toLowerCase()}`}
                                                    />
                                                    {/* REQ-11: Effective / Ineffective verdict toggle (Design variant). */}
                                                    <div className="response-verdict-toggle">
                                                        {(['Effective', 'Ineffective'] as const).map((v) => (
                                                            <button
                                                                key={v}
                                                                type="button"
                                                                className={`response-verdict-btn response-verdict-btn--${v.toLowerCase()}${s.verdict === v ? ' response-verdict-btn--active' : ''}`}
                                                                disabled={locked}
                                                                data-testid={`BTN_sop_verdict_${s.score_id}_${v.toLowerCase()}`}
                                                                onClick={() => updateStep(s, { verdict: s.verdict === v ? null : v })}
                                                            >
                                                                {v}
                                                            </button>
                                                        ))}
                                                    </div>
                                                </AssessmentPanel>
                                            }
                                            actions={
                                                <div className="seamless__bar">
                                                    <button
                                                        type="button"
                                                        className={`response-row-action${s.remarks ? ' response-row-action--filled' : ''}`}
                                                        title={s.remarks ? 'Edit observations' : 'Add observations'}
                                                        data-testid={`BTN_sop_step_score_${s.score_id}_remarks`}
                                                        onClick={() => setRemarksStep(s.score_id)}
                                                        disabled={locked}
                                                    >
                                                        <MessageSquare size={16} />
                                                    </button>
                                                    {s.remarks && <span className="response-action-dot response-action-dot--brand" aria-hidden="true" />}
                                                </div>
                                            }
                                            impl={
                                                <>
                                                    {risks.length === 0 && (
                                                        <p className="seamless__impl-sub" style={{ padding: '8px 0' }}>No risks mapped to this sub-process.</p>
                                                    )}
                                                    {risks.map((rg, ri) => {
                                                        const rk = `${s.score_id}-${rg.risk.risk_id}`;
                                                        const rv = riskState[rk] ?? EMPTY_RISK;
                                                        const lik = rv.likelihood === '' ? 0 : rv.likelihood;
                                                        const sev = rv.impact === '' ? 0 : rv.impact;
                                                        const inherent = lik * sev;
                                                        const rEx = rv.existence === '' ? 0 : rv.existence;
                                                        const rEf = rv.effectiveness === '' ? 0 : rv.effectiveness;
                                                        const effPct = (rEx * rEf) / 25;
                                                        const residual = Math.round(inherent * (1 - effPct));
                                                        return (
                                                            <SeamlessCard
                                                                key={rk}
                                                                sno={ri + 1}
                                                                snoLabel={`R${ri + 1}`}
                                                                variant="risk"
                                                                /* code (HR-RISK-…) removed — parity with audit ToD */
                                                                title={rg.risk.risk_name ?? 'Unknown risk'}
                                                                inlineRemark={
                                                                    <InlineIssueMeta
                                                                        label="Vulnerabilities"
                                                                        issues={vulnsByRisk[rg.risk.risk_id] || []}
                                                                        testIdPrefix={`CHIP_sop_vuln_${s.score_id}_${rg.risk.risk_id}`}
                                                                    />
                                                                }
                                                                assessment={
                                                                    <DualScorePanel
                                                                        row1={
                                                                            <>
                                                                                <AssessRow
                                                                                    label="Lik" labelTitle="Likelihood" tone="risk"
                                                                                    value={rv.likelihood} disabled={locked}
                                                                                    onChange={(v) => saveRisk(s, rg.risk.risk_id, { likelihood: v })}
                                                                                    testId={`SEL_sop_risk_likelihood_${s.score_id}_${rg.risk.risk_id}`}
                                                                                />
                                                                                <AssessRow
                                                                                    label="Sev" labelTitle="Severity" tone="risk"
                                                                                    value={rv.impact} disabled={locked}
                                                                                    onChange={(v) => saveRisk(s, rg.risk.risk_id, { impact: v })}
                                                                                    testId={`SEL_sop_risk_severity_${s.score_id}_${rg.risk.risk_id}`}
                                                                                />
                                                                            </>
                                                                        }
                                                                        verdict1={
                                                                            <NetScore
                                                                                text={`${inherent}`}
                                                                                variant={riskBand(inherent)}
                                                                                title={`Inherent risk = Likelihood × Severity = ${inherent} (out of 25)`}
                                                                                testId={`LBL_sop_risk_inherent_${s.score_id}_${rg.risk.risk_id}`}
                                                                            />
                                                                        }
                                                                        row2={
                                                                            <>
                                                                                <AssessRow
                                                                                    label="Ex" labelTitle="Existence (control)" tone="control"
                                                                                    value={rv.existence} disabled={locked}
                                                                                    onChange={(v) => saveRisk(s, rg.risk.risk_id, { existence: v })}
                                                                                    testId={`SEL_sop_risk_existence_${s.score_id}_${rg.risk.risk_id}`}
                                                                                />
                                                                                <AssessRow
                                                                                    label="Ef" labelTitle="Effectiveness (control)" tone="control"
                                                                                    value={rv.effectiveness} disabled={locked}
                                                                                    onChange={(v) => saveRisk(s, rg.risk.risk_id, { effectiveness: v })}
                                                                                    testId={`SEL_sop_risk_effectiveness_${s.score_id}_${rg.risk.risk_id}`}
                                                                                />
                                                                            </>
                                                                        }
                                                                        verdict2={
                                                                            <NetScore
                                                                                text={`${residual}/25`}
                                                                                variant={riskBand(residual)}
                                                                                title={`Residual = Inherent ${inherent} × (1 − ${Math.round(effPct * 100)}% control offset) = ${residual}`}
                                                                                testId={`LBL_sop_risk_residual_${s.score_id}_${rg.risk.risk_id}`}
                                                                                emphasis
                                                                            />
                                                                        }
                                                                    />
                                                                }
                                                                impl={
                                                                    <>
                                                                        {rg.controls.length === 0 && (
                                                                            <p className="seamless__impl-sub" style={{ padding: '8px 0' }}>No mitigating controls.</p>
                                                                        )}
                                                                        {rg.controls.map((c, ci) => {
                                                                            const ck = `${s.score_id}-${rg.risk.risk_id}-${c.control_id}`;
                                                                            const cv = ctrlState[ck] ?? EMPTY_CTRL;
                                                                            const net = (cv.existence === '' ? 0 : cv.existence) * (cv.effectiveness === '' ? 0 : cv.effectiveness);
                                                                            const implItems = (c.implementations ?? []).map(im => {
                                                                                const ik = `${s.score_id}-${im.id}`;
                                                                                return {
                                                                                    id: im.id,
                                                                                    sno: im.sequence_number,
                                                                                    title: im.target_label || im.explanation || `Implementation ${im.id}`,
                                                                                    sub: im.implementation_type,
                                                                                    result: implState[ik]?.result ?? '',
                                                                                    observations: '',
                                                                                };
                                                                            });
                                                                            return (
                                                                                <SeamlessCard
                                                                                    key={ck}
                                                                                    sno={ci + 1}
                                                                                    snoLabel={`C${ci + 1}`}
                                                                                    variant="control"
                                                                                    title={c.control_name ?? 'Unknown control'}
                                                                                    typeChip={<TypeChip label={c.control_type} />}
                                                                                    inlineRemark={
                                                                                        <InlineIssueMeta
                                                                                            label="Deficiencies"
                                                                                            issues={defsByControl[c.control_id] || []}
                                                                                            testIdPrefix={`CHIP_sop_deficiency_${s.score_id}_${c.control_id}`}
                                                                                        />
                                                                                    }
                                                                                    assessment={
                                                                                        <AssessmentPanel
                                                                                            verdict={
                                                                                                <NetScore
                                                                                                    text={`${net}/25`}
                                                                                                    variant={scoreBand(net)}
                                                                                                    title="Net Score = Existence × Effectiveness (out of 25)"
                                                                                                    testId={`LBL_sop_control_net_${s.score_id}_${rg.risk.risk_id}_${c.control_id}`}
                                                                                                />
                                                                                            }
                                                                                        >
                                                                                            <AssessRow
                                                                                                label="Ex" labelTitle="Existence" tone="control"
                                                                                                value={cv.existence} disabled={locked}
                                                                                                onChange={(v) => saveCtrl(s, rg.risk.risk_id, c.control_id, { existence: v })}
                                                                                                testId={`SEL_sop_control_existence_${s.score_id}_${rg.risk.risk_id}_${c.control_id}`}
                                                                                            />
                                                                                            <AssessRow
                                                                                                label="Ef" labelTitle="Effectiveness" tone="control"
                                                                                                value={cv.effectiveness} disabled={locked}
                                                                                                onChange={(v) => saveCtrl(s, rg.risk.risk_id, c.control_id, { effectiveness: v })}
                                                                                                testId={`SEL_sop_control_effectiveness_${s.score_id}_${rg.risk.risk_id}_${c.control_id}`}
                                                                                            />
                                                                                        </AssessmentPanel>
                                                                                    }
                                                                                    impl={
                                                                                        implItems.length > 0 ? (
                                                                                            <>
                                                                                                <SectionLabel>Implementations</SectionLabel>
                                                                                                <ImplList
                                                                                                    items={implItems}
                                                                                                    onResult={(id, result) => saveImpl(s, c.control_id, id, result as ImplVerdict)}
                                                                                                    onObservations={() => {}}
                                                                                                    disabled={locked}
                                                                                                    testIdPrefix={`sop_impl_${s.score_id}_${c.control_id}`}
                                                                                                />
                                                                                            </>
                                                                                        ) : undefined
                                                                                    }
                                                                                />
                                                                            );
                                                                        })}
                                                                    </>
                                                                }
                                                            />
                                                        );
                                                    })}
                                                </>
                                            }
                                        />
                                    );
                                })}
                            </SeamlessSurface>
                            </div>
                        )}
                    </div>
                )}

                {/* Observation modal — captures the per-step observation (checklist parity). */}
                {remarksStep !== null && detail && (() => {
                    const s = detail.steps.find(x => x.score_id === remarksStep);
                    if (!s) return null;
                    return (
                        <Modal isOpen onClose={() => setRemarksStep(null)} size="md">
                            <Modal.Header onClose={() => setRemarksStep(null)}>Observations</Modal.Header>
                            <Modal.Body>
                                <p className="response-remarks-question">{s.step_name}</p>
                                <FormControl.Textarea
                                    data-testid={`INP_sop_step_score_${s.score_id}_observations`}
                                    label="Observations"
                                    value={s.remarks || ''}
                                    rows={5}
                                    autoFocus
                                    disabled={locked}
                                    placeholder="Observations…"
                                    onChange={locked ? undefined : (e: React.ChangeEvent<HTMLTextAreaElement>) => {
                                        setObsErrors(prev => { const n = { ...prev }; delete n[s.score_id]; return n; });
                                        setDetail(prev => prev ? {
                                            ...prev,
                                            steps: prev.steps.map(x => x.score_id === s.score_id ? { ...x, remarks: e.target.value } : x),
                                        } : prev);
                                    }}
                                    onBlur={locked ? undefined : () => handleObsBlur(s)}
                                />
                                {obsErrors[s.score_id] && <div className="response-inline-warning">{obsErrors[s.score_id]}</div>}
                            </Modal.Body>
                            <Modal.Footer>
                                <Button variant="fill" onClick={() => setRemarksStep(null)}>Done</Button>
                            </Modal.Footer>
                        </Modal>
                    );
                })()}
                <Toast toast={toast} onClose={() => setToast(null)} />
            </Modal.Body>
            <Modal.Footer>
                {/* Wait for detail to load before deciding the footer — otherwise
                    the checker's Reject/Approve buttons flash for one render
                    against a null detail, then vanish once the loaded status
                    (e.g. Approved) flips the footer to its terminal label. */}
                {!detail ? null : isApproved ? (
                    // Approval is terminal — once approved the footer shows only the
                    // "Approved" badge. No Reject (a checker can't bounce an already
                    // approved program back from here).
                    <span className="status-badge success" style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                        <Check size={14} /> Approved
                    </span>
                ) : (
                    <>
                        {!readOnly && !isChecker && (
                            <Button variant="fill" icon={Send} onClick={submit} disabled={busy || !allDone || detail?.status === 'Complete'}>
                                {busy ? 'Submitting…' : detail?.status === 'Complete' ? 'Submitted' : 'Submit for Approval'}
                            </Button>
                        )}
                        {!readOnly && isChecker && (
                            <>
                                <Button variant="danger" icon={X} onClick={() => { setRejectReason(''); setRejectErr(null); setShowReject(true); }} disabled={busy || detail?.status !== 'Complete'}>
                                    {busy ? 'Rejecting…' : 'Reject'}
                                </Button>
                                <Button variant="update" icon={Check} onClick={approve} disabled={busy || detail?.status !== 'Complete'}>
                                    {busy ? 'Approving…' : 'Approve'}
                                </Button>
                            </>
                        )}
                    </>
                )}
            </Modal.Footer>

            {/* Checker reject-reason modal — the note is required and is shown to
                the maker on the bounced-back program. */}
            {showReject && (
                <Modal isOpen size="md" onClose={() => setShowReject(false)}>
                    <Modal.Header onClose={() => setShowReject(false)}>Reject — add a note</Modal.Header>
                    <Modal.Body>
                        <p className="response-remarks-question">
                            Send this back to the maker. State why it is being rejected
                        </p>
                        <FormControl.Textarea
                            label=""
                            rows={5}
                            noMargin
                            value={rejectReason}
                            placeholder="Reason for rejection (required)"
                            data-testid="INP_sop_program_reject_reason"
                            error={rejectErr || undefined}
                            autoFocus
                            onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => { setRejectReason(e.target.value); if (rejectErr) setRejectErr(null); }}
                        />
                    </Modal.Body>
                    <Modal.Footer>
                        <Button variant="outline" onClick={() => setShowReject(false)} disabled={busy} data-testid="BTN_sop_program_reject_cancel">Cancel</Button>
                        <Button variant="danger" icon={X} onClick={reject} disabled={busy || !rejectReason.trim()} data-testid="BTN_sop_program_reject_confirm">
                            {busy ? 'Rejecting…' : 'Reject'}
                        </Button>
                    </Modal.Footer>
                </Modal>
            )}
        </Modal>
    );
};

const WorkProcessWalkthroughsBody: React.FC<{
    variant: SopVariant;
    scope?: 'active' | 'archive';
    readOnly?: boolean;
}> = ({ variant, scope = 'active', readOnly = false }) => {
    const { state } = useAuth();
    const { formatDate } = useEntitySettings();
    const myId = state.user?.id;
    const [rows, setRows] = useState<SopProgram[]>([]);
    const [loading, setLoading] = useState(true);
    const [openId, setOpenId] = useState<number | null>(null);
    const [openIsChecker, setOpenIsChecker] = useState(false);
    const [toast, setToast] = useState<ToastMessage | null>(null);
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [searchTerm, setSearchTerm] = useState('');

    // Deep-link from calendar drill-down:
    //   /work?tab=process-walkthroughs-design&open=<id>     (Test of Design)
    //   /work?tab=process-walkthroughs-compliance&open=<id> (Test of Compliance)
    const [searchParams, setSearchParams] = useSearchParams();
    useEffect(() => {
        const o = searchParams.get('open');
        if (!o) return;
        const id = parseInt(o, 10);
        if (Number.isFinite(id)) setOpenId(id);
        const next = new URLSearchParams(searchParams);
        next.delete('open');
        setSearchParams(next, { replace: true });
    }, [searchParams, setSearchParams]);

    const refresh = useCallback(async () => {
        setLoading(true);
        try {
            const [makerRows, checkerRows] = await Promise.all([
                sopProgramService.list({ assigned_to_me: true, variant }),
                sopProgramService.list({ checker_for_me: true, variant }),
            ]);
            const map = new Map<number, SopProgram>();
            for (const r of [...makerRows, ...checkerRows]) map.set(r.id, r);
            setRows(Array.from(map.values()));
        } catch (e: any) {
            setToast({ type: 'error', message: extractApiError(e, 'Failed to load programs') });
        } finally {
            setLoading(false);
        }
    }, [variant]);
    useEffect(() => { refresh(); }, [refresh]);
    useEffect(() => { setPage(1); }, [searchTerm, pageSize]);

    const scoped = visibleForScope(rows, p => isClosedProgram(p.status), scope);
    const filtered = scoped.filter(p =>
        !searchTerm ||
        p.program_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        p.location_name?.toLowerCase().includes(searchTerm.toLowerCase())
    );
    const totalRecords = filtered.length;
    const paginatedRows = filtered.slice((page - 1) * pageSize, page * pageSize);

    return (
        <div className="netbody-card flex flex-col h-full">
            <div className="netbody-header">
                <div className="flex items-center gap-2 ml-auto">
                    <FormControl.Input
                        type="text"
                        placeholder="Search…"
                        value={searchTerm}
                        onChange={e => setSearchTerm(e.target.value)}
                        leadingIcon={<Search size={14} />}
                        className="filter-search"
                    />
                </div>
            </div>

            <div className="netbody-content">
                <div className="scroll-container flex-1 overflow-hidden">
                    <div className="table-list">
                        <div className="table-list-scroll">
                            <table className="table-list-table">
                                <thead>
                                    <tr>
                                        <th className="table-list-th table-list-cell--sno">S.No</th>
                                        <th className="table-list-th">Program</th>
                                        <th className="table-list-th">Role</th>
                                        <th className="table-list-th">Geography</th>
                                        <th className="table-list-th">Window</th>
                                        <th className="table-list-th table-list-cell--status table-list-th--right">Status</th>
                                    </tr>
                                </thead>
                                <tbody aria-busy={loading} aria-live="polite">
                                    {loading && (
                                        <Skeleton.TableRows cols={['2xs', 'md', 'pill', 'md', 'sm', { width: 'pill', align: 'right' }]} />
                                    )}
                                    {!loading && filtered.length === 0 && (
                                        <tr>
                                            <td colSpan={6} className="table-list-cell">
                                                {scope === 'archive' ? (
                                                    <NoDataFound
                                                        title="No closed process walkthroughs."
                                                        message={`No closed Test of ${variant} programs.`}
                                                    />
                                                ) : (
                                                    <NoDataFound
                                                        title={`No Test of ${variant} Programs Found`}
                                                        message={`No Test of ${variant} programs are assigned to you.`}
                                                    />
                                                )}
                                            </td>
                                        </tr>
                                    )}
                                    {!loading && paginatedRows.map((p, idx) => {
                                        const isChecker = p.checker_id === myId && p.maker_id !== myId;
                                        return (
                                            <tr key={p.id} className="table-list-row"
                                                onClick={() => { setOpenIsChecker(isChecker); setOpenId(p.id); }}>
                                                <td className="table-list-cell table-list-cell--sno">{(page - 1) * pageSize + idx + 1}</td>
                                                <td className="table-list-cell">
                                                    <span className="table-list-cell-title">{p.program_name}</span>
                                                    {p.sop_name && <span className="table-list-cell-sub">{p.sop_name}</span>}
                                                </td>
                                                <td className="table-list-cell">
                                                    <span className={isChecker ? 'status-badge sm warning' : 'status-badge sm info'}>
                                                        {isChecker ? 'Checker' : 'Maker'}
                                                    </span>
                                                </td>
                                                <td className="table-list-cell">{p.location_name || '—'}</td>
                                                <td className="table-list-cell">
                                                    <span className="text-xs text-gray-600">{formatDate(p.start_date)} → {formatDate(p.end_date)}</span>
                                                </td>
                                                <td className="table-list-cell table-list-cell--right">
                                                    <span className={statusBadgeCss(p.status)}>
                                                        {p.status || 'Not-Started'}
                                                    </span>
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                        <Pagination
                            page={page}
                            pageSize={pageSize}
                            totalRecords={totalRecords}
                            onPageChange={setPage}
                            onPageSizeChange={setPageSize}
                            recordLabel="programs"
                        />
                    </div>
                </div>
            </div>

            <Toast toast={toast} onClose={() => setToast(null)} />

            {openId !== null && (
                <ExecutionModal
                    programId={openId}
                    isChecker={openIsChecker}
                    readOnly={readOnly}
                    onClose={() => setOpenId(null)}
                    onSaved={refresh}
                />
            )}
        </div>
    );
};

export default WorkProcessWalkthroughsBody;
